<template>
    <RadioGroupRoot
        v-bind="field?.controlAttrs.value"
        :orientation="orientation"
        :disabled="disabled"
        :aria-labelledby="field?.labelId ?? (label ? labelId : undefined)"
        class="radio-group"
        :class="{ 'is-card': appearance === 'cards' }"
        :style="appearance === 'cards' ? { '--radio-options-count': orientation === 'horizontal' ? options.length : 1 } : undefined">
        <span v-if="label" :id="labelId" class="group-label">{{ label }}</span>
        <label v-for="option of options" :key="String(option.value)" :for="itemIds[String(option.value)]" class="option" :class="{ 'is-disabled': option.disabled || disabled }">
            <RadioGroupItem :id="itemIds[String(option.value)]" :value="option.value" :disabled="option.disabled" class="radio">
                <span v-if="appearance === 'cards'">{{ option.label }}</span>
                <RadioGroupIndicator v-else class="indicator" />
            </RadioGroupItem>
            <span v-if="appearance === 'default'" class="option-label">{{ option.label }}</span>
        </label>
    </RadioGroupRoot>
</template>

<script setup lang="ts">
import { type AcceptableValue, RadioGroupIndicator, RadioGroupItem, RadioGroupRoot } from "reka-ui";
import { computed, useId } from "vue";

import { useFormField } from "./formFieldContext";

export type RadioGroupOrientation = "vertical" | "horizontal";

export type RadioGroupOption = {
    value: AcceptableValue;
    label: string;
    disabled?: boolean;
};

type BaseRadioGroupProps = {
    options: RadioGroupOption[];
    label?: string;
    orientation?: RadioGroupOrientation;
    disabled?: boolean;
    appearance?: "default" | "cards";
};

const { options, label, orientation = "vertical", disabled = false, appearance = "default" } = defineProps<BaseRadioGroupProps>();

const labelId = useId();

// Inside a FormField the group is named by the field's label (a `for` cannot reach a radiogroup)
// and takes its id, describedby and invalid wiring. An `id` or `aria-labelledby` passed as an
// attribute still wins, since fallthrough attrs override these bindings.
const field = useFormField();

const itemIds = computed<Record<string, string>>(() => Object.fromEntries(options.map((option) => [String(option.value), `${labelId}-${String(option.value)}`])));
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .radio-group {
        @apply flex flex-col gap-3 aria-[orientation=horizontal]:flex-row aria-[orientation=horizontal]:flex-wrap;
    }

    .group-label {
        @apply body-small-bold text-control-label;
    }

    .option {
        @apply flex flex-row items-center gap-3;
    }

    .option.is-disabled {
        @apply cursor-not-allowed;
    }

    .radio {
        @apply flex h-5 min-w-5 max-w-5 shrink-0 appearance-none items-center justify-center rounded-full border border-control-border-strong bg-control-bg transition-colors data-[state=checked]:border-control-accent data-disabled:cursor-not-allowed data-disabled:opacity-50;
    }

    .indicator {
        @apply flex h-full w-full items-center justify-center after:block after:h-2.5 after:w-2.5 after:rounded-full after:bg-control-accent after:content-[''];
    }

    .option-label {
        @apply body select-none text-control-label;
    }

    .option.is-disabled .option-label {
        @apply text-control-label-disabled;
    }

    .radio-group.is-card {
        @apply grid gap-2;
        grid-template-columns: repeat(var(--radio-options-count), minmax(0, 1fr));
    }

    .is-card .group-label {
        @apply col-span-full body-small-semibold;
    }

    .is-card .radio {
        @apply min-h-11 h-auto w-full min-w-0 max-w-none rounded-lg px-3 py-2 body-small-semibold text-control-text-strong border-control-border cursor-pointer hover:bg-surface-hover focus-field;
        &[data-state="checked"] {
            @apply border-control-accent bg-select-option-selected text-control-text-strong ring-1 ring-control-accent;
        }
        &:focus-visible {
            outline: 2px solid var(--color-control-accent);
            outline-offset: 2px;
        }
    }

    /* BaseInput's invalid border, on every choice not yet picked; declared last so it also
       overrides the card border. */
    .radio-group[aria-invalid="true"] .radio:not([data-state="checked"]) {
        @apply border-status-error;
    }
}
</style>
